// NOTE: Move this into a separate static config file is this is extended beyond simply param object config
const CONFIG = {
  product: {
    resultsPerPage: 24,
  },
  browse: {
    fmtOptions: {
      groups_max_depth: 3,
    },
  },
  recommendations: {
    numResults: 12,
  },
  autocomplete: {
    resultsPerSection: {
      Products: 5,
      'Search Suggestions': 10,
      Categories: 5,
      Content: 5,
    },
  },
  popularSearchTerms: {
    numResults: 5,
  },
  pairItWith: {
    numResults: 5,
  },
}

export const constructorIoService = () => {
  if (!import.meta.client) {
    return {}
  }

  const { $i18n, $constructor } = useNuxtApp()
  const userStore = useUserStore()

  const client = $constructor({
    locale: $i18n.locale.value,
    userId: userStore.user?.customerId || userStore.guestUser?.customerId,
    isMember: userStore.isLoggedIn,
  })

  const getSearchResults = async (query: string, parameters = {}) => {
    try {
      const response = await client.search.getSearchResults(query, { ...parameters })
      return response.response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot getSearchResults from Constructor API', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getBrowseResults = async (sectionId: string, parameters: any = {}) => {
    try {
      const response = await client.browse.getBrowseResults('group_id', sectionId, { ...parameters })
      return response.response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot getBrowseResults from Constructor API', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getBrowseGroups = async (filters: Object = {}) => {
    try {
      const response = await client.browse.getBrowseGroups({
        filters,
        fmtOptions: CONFIG.browse.fmtOptions,
      })
      return response.response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot getBrowseGroups from Constructor API', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getAutocompleteResults = async (query: string, filters: Object = {}) => {
    try {
      const response = await client.autocomplete.getAutocompleteResults(query, {
        resultsPerSection: CONFIG.autocomplete.resultsPerSection,
        filters,
      })
      return response.sections
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot getBrowseResults from Constructor API', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getRecommendations = async (
    itemId: string,
    itemIds: Array<string> = [],
    filters: Object = {},
    numResults?: number,
  ) => {
    try {
      const response = await client.recommendations.getRecommendations(itemId, {
        numResults: numResults ?? CONFIG.recommendations.numResults,
        itemIds,
        filters,
      })
      return response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot initialise recommendations', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getPopularSearchTerms = async () => {
    try {
      // The Pod ID `popular-searches` is the default from Constructor to get popular search terms
      const response = await client.recommendations.getRecommendations('popular-searches', {
        numResults: CONFIG.popularSearchTerms.numResults,
        section: 'Search Suggestions',
      })
      return response.response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot initialise popular search terms', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  const getPairItWithRecommendation = async (itemId: string, itemIds: Array<string> = [], filters: Object = {}) => {
    try {
      const response = await client.recommendations.getRecommendations(itemId, {
        numResults: CONFIG.pairItWith.numResults,
        itemIds,
        filters,
      })
      return response
    } catch (e) {
      if (e instanceof Error) {
        throw new Error('Cannot initialise pair it with recommendation', e)
      } else {
        throw new Error('An unknown error occurred')
      }
    }
  }

  return {
    getSearchResults,
    getBrowseResults,
    getBrowseGroups,
    getAutocompleteResults,
    getRecommendations,
    getPopularSearchTerms,
    getPairItWithRecommendation,
  }
}
